import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import PulseWaveLogo from '../../assets/svg-pulse-wave-logo';
import { URLS } from '../../router-x/routes';
import { SendResetPasswordEmail } from '../../services/firebase/auth';
import { RequestPasswordReset } from '../../services/backend';
import { useAuth } from '../../services/contexts/authContext';
import { getHomePageRedirectUrl } from '../../utils/functions';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, userDetails, loading: authLoading } = useAuth();

  useEffect(() => {
    if (user && userDetails && !authLoading) {
      navigate(getHomePageRedirectUrl(userDetails.role));
    }
  }, [user, userDetails, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Check account status before sending reset email.
      await RequestPasswordReset(normalizedEmail);

      // If account is active, proceed with Firebase reset email.
      await SendResetPasswordEmail(normalizedEmail);

      setSuccess('If your email is registered, a reset link has been sent. Please check your inbox.');
    } catch (err) {
      if (err?.response?.status === 403) {
        setError('Your account is disabled. Please contact an admin.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to send reset email. Please try again.');
      }
      console.error('Forgot password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-6"
      style={{ background: 'radial-gradient(120% 90% at 80% 8%, #C9651B 0%, #9A480E 44%, #4A2E1A 100%)' }}
    >
      {/* Decorative rings */}
      <div className="pointer-events-none absolute -left-[6%] -top-[16%] h-[560px] w-[560px] rounded-full border border-white/[0.09]" />
      <div className="pointer-events-none absolute -bottom-[18%] right-[4%] h-[480px] w-[480px] rounded-full border border-white/[0.06]" />

      <div className="relative w-full max-w-[430px] animate-popIn rounded-[22px] bg-white p-9 shadow-[0_30px_70px_-30px_rgba(40,22,8,0.65)] sm:p-10">
        <div className="flex flex-col items-center">
          <PulseWaveLogo width={98} height={50} />
          <div className="mt-4 pl-[6px] font-display text-[27px] font-bold tracking-[0.34em] text-ink">
            PULSE
          </div>
          <div className="mt-1.5 font-display text-[13.5px] font-semibold text-brand">
            Workspace Management System
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className="font-display text-lg font-bold text-ink">Forgot password?</div>
          <div className="mt-1 text-[13px] text-[#8C8175]">
            Enter your email and we&rsquo;ll send a reset link.
          </div>
        </div>

        {error && (
          <div className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mt-5 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">
              Email Address <span className="text-brand">*</span>
            </label>
            <div className="flex h-12 items-center gap-2.5 rounded-xl border-[1.5px] border-cream-200 bg-cream px-3.5 transition-colors focus-within:border-brand">
              <Mail size={18} className="shrink-0 text-[#B7AC9E]" />
              <input
                id="email"
                name="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent text-sm text-[#2B2420] outline-none placeholder:text-[#B7AC9E]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-brand font-display text-[15px] font-semibold text-white shadow-[0_10px_20px_-10px_rgba(190,83,16,0.7)] transition-all hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-[0_16px_28px_-10px_rgba(190,83,16,0.85)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Sending...</span>
              </>
            ) : 'Send Reset Link'}
          </button>
        </form>

        <Link
          to={URLS.ADMIN.AUTH}
          className="mt-4 flex items-center justify-center gap-1.5 text-[13px] font-semibold text-[#6B675F] transition-colors hover:text-brand"
        >
          <ArrowLeft size={16} />
          Back to Sign In
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
